import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { setSession } from '@/lib/session';

interface AuthResponse {
  user: { id: string; name: string };
  token: string;
  workspaceId?: string;
}

export default function Signup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      const data = await apiFetch<AuthResponse>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(form),
        skipAuth: true,
      });
      setSession({ token: data.token, userName: data.user.name, workspaceId: data.workspaceId });
      toast.success(t('signup.success', "Account created! Let's set up your workspace."));
      navigate('/onboarding');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 flex items-center justify-center py-16 px-4">
        <Card className="w-full max-w-md p-8 shadow-xl">
          <h1 className="text-2xl font-bold mb-1">{t('signup.title', 'Create your account')}</h1>
          <p className="text-sm text-muted-foreground mb-6">
            {t('signup.subtitle', 'Start automating your real estate campaigns.')}
          </p>
          <form className="space-y-4" onSubmit={handleSignup}>
            <div className="grid gap-2">
              <Label htmlFor="name">{t('signup.name', 'Full name')}</Label>
              <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t('login.email', 'Email')}</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t('login.password', 'Password')}</Label>
              <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('checkout.loading', 'Please wait…') : t('signup.cta', 'Create account')}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t('signup.have_account', 'Already have an account?')}{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">{t('nav.login', 'Log in')}</Link>
          </p>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
